/**
 * LiveSessionBanner — Home & Group Overview "Sessions Rail".
 *
 * iter110 upgrade:
 *   - Switched from /sessions/live (live-only) to /sessions/my-rail which
 *     returns up to 5 sessions across 3 tiers: live | rsvp_open | opening_soon.
 *   - Each tier renders with its own visual treatment:
 *       live          → red pulsing pill with elapsed timer
 *       rsvp_open     → green pill with inline "I'm IN" / "OUT" buttons + close countdown
 *       opening_soon  → muted amber pill ("Opens in 4h 22m")
 *   - 30s polling + SSE refresh on session_created / rsvp_changed events.
 *   - Click outside the action buttons → deep-links to the focused session card.
 *
 * Backward-compatible props: variant="home" | "group", groupId (filter).
 * Test IDs:
 *   data-testid="sessions-rail-home" / "sessions-rail-group"
 *   data-testid="rail-card-{tier}-{session_id}"
 *   data-testid="rail-rsvp-in-{session_id}" / "rail-rsvp-out-{session_id}"
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card } from './ui/card';
import { Zap, ChevronRight, Clock, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { useRealtime } from '../context/RealtimeContext';
import useSessionAlerts from '../hooks/useSessionAlerts';
import CountdownChip from './CountdownChip';

function formatRsvpClock(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch {
    return null;
  }
}

function secondsUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso).getTime() - Date.now();
  if (d <= 0) return 0;
  return Math.floor(d / 1000);
}

const SESSIONS_RAIL_EVENTS = [
  'session_created',
  'session_cancelled',
  'session_started',
  'rsvp_changed',
];

function formatElapsed(startIso) {
  if (!startIso) return '';
  const elapsedMs = Date.now() - new Date(startIso).getTime();
  if (elapsedMs < 0) return 'starting';
  const totalMin = Math.floor(elapsedMs / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m elapsed` : `${m}m elapsed`;
}

function liveStyleClass(style) {
  switch (style) {
    case 'green_glow':
      return 'border-2 border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/30 shadow-[0_0_20px_-4px_rgba(16,185,129,0.5)]';
    case 'gradient_banner':
      return 'border-2 border-amber-400/40 bg-gradient-to-br from-red-50 via-amber-50 to-emerald-50 dark:from-red-950/30 dark:via-amber-950/20 dark:to-emerald-950/30';
    case 'minimal':
      return 'border-2 border-red-500/50 bg-red-50/40 dark:bg-red-950/20';
    case 'red_pulse':
    default:
      return 'border-2 border-red-500 bg-red-50/60 dark:bg-red-950/30 shadow-[0_0_20px_-4px_rgba(239,68,68,0.6)] animate-pulse';
  }
}

const TIER_TONE = {
  live: 'live',
  rsvp_open: 'open',
  opening_soon: 'soon',
};

function openCardClass(tone) {
  if (tone === 'open') {
    return 'border-2 border-emerald-500/50 bg-emerald-50/40 dark:bg-emerald-950/20';
  }
  return 'border-2 border-amber-400/40 bg-amber-50/30 dark:bg-amber-950/15';
}

export default function LiveSessionBanner({ variant = 'home', groupId }) {
  const [rail, setRail] = useState([]);
  const [tick, setTick] = useState(0);
  const [acting, setActing] = useState(null);
  const navigate = useNavigate();
  const { onEvent } = useRealtime();

  const fetchRail = useCallback(async () => {
    try {
      const res = await api.get('/sessions/my-rail');
      const all = res.data?.sessions || [];
      const filtered = groupId ? all.filter((s) => s.group_id === groupId) : all;
      setRail(filtered);
    } catch {
      /* silent — banner is non-critical UX */
    }
  }, [groupId]);

  useEffect(() => {
    fetchRail();
    const id = setInterval(fetchRail, 30_000);
    return () => clearInterval(id);
  }, [fetchRail]);

  // Re-fetch immediately when a session-state-changing SSE event fires.
  useEffect(() => onEvent(SESSIONS_RAIL_EVENTS, fetchRail), [onEvent, fetchRail]);

  // iter114 — fire vibrate+notification at T-60s/T-10s for sessions the user
  // has RSVPed "In" for.
  useSessionAlerts(rail);

  // Tick adaptively — every 1s when ANY card's milestone is < 1h away,
  // otherwise every 60s. iter112.
  const needsFastTick = useMemo(() => {
    const now = Date.now();
    return rail.some((s) => {
      const candidates = [];
      if (s.tier === 'opening_soon' && s.rsvp_opens_at) {
        candidates.push(new Date(s.rsvp_opens_at).getTime() - now);
      }
      if (s.tier === 'rsvp_open' && s.rsvp_closes_at) {
        candidates.push(new Date(s.rsvp_closes_at).getTime() - now);
      }
      if ((s.tier === 'rsvp_open' || s.tier === 'opening_soon') && s.start_iso) {
        candidates.push(new Date(s.start_iso).getTime() - now);
      }
      return candidates.some((d) => d > 0 && d <= 3_600_000);
    });
  }, [rail]);

  useEffect(() => {
    const intervalMs = needsFastTick ? 1_000 : 60_000;
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [needsFastTick]);

  const goToSession = (s) => {
    // iter117 — dedicated session detail page replaces the old deep-link
    // drill-down through Group → People → Sessions sub-tab.
    navigate(`/groups/${s.group_id}/sessions/${s.session_id}`);
  };

  const handleRsvp = async (s, status) => {
    if (acting) return;
    setActing(`${s.session_id}:${status}`);
    try {
      await api.post(`/sessions/${s.session_id}/rsvp`, { action: status });
      toast.success(status === 'in' ? "You're IN" : 'Marked OUT');
      fetchRail();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'RSVP failed');
    } finally {
      setActing(null);
    }
  };

  // Memoise the rendered list so countdown re-ticks don't re-key cards.
  const cards = useMemo(() => rail, [rail]);

  if (!cards.length) return null;

  const containerTestId = variant === 'group' ? 'sessions-rail-group' : 'sessions-rail-home';

  return (
    <div className="space-y-2" data-testid={containerTestId}>
      {cards.map((s) => {
        if (s.tier === 'live') {
          return <LiveCard key={s.session_id} s={s} variant={variant} onClick={() => goToSession(s)} />;
        }
        if (s.tier === 'rsvp_open') {
          return (
            <RsvpOpenCard
              key={s.session_id}
              s={s}
              variant={variant}
              acting={acting}
              onCardClick={() => goToSession(s)}
              onIn={() => handleRsvp(s, 'in')}
              onOut={() => handleRsvp(s, 'out')}
            />
          );
        }
        return <OpeningSoonCard key={s.session_id} s={s} variant={variant} onClick={() => goToSession(s)} />;
      })}
    </div>
  );
}

/* ------------------------- Group health (quorum ring) ------------------------- */
/**
 * Small circular ring chip showing confirmed_count / min_players progress.
 * Colour heuristic — combines fill ratio with time-pressure:
 *   ratio ≥ 1                              → green   (quorum met)
 *   ratio ≥ 0.6  OR (any progress, plenty of time)  → amber
 *   ratio < 0.6 AND closing in <2h         → red     (at risk)
 *   ratio = 0                              → red     (no one yet)
 * Compact (24×24) so it fits inline next to the chevron.
 */
function QuorumRing({ confirmed, min, total, deadlineIso }) {
  const denom = total && total > 0 ? total : min;
  if (denom == null || denom <= 0) return null;
  // Ring fill = confirmed / total members; colour still driven by quorum (min_players)
  const fillRatio = Math.max(0, Math.min(1, (confirmed || 0) / denom));
  const ratio = min > 0 ? Math.max(0, Math.min(1, (confirmed || 0) / min)) : fillRatio;
  const radius = 9;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - fillRatio);

  // Time-pressure: how many minutes until the deadline (rsvp_closes_at or session start)?
  const minutesLeft = deadlineIso ? Math.max(0, Math.floor((new Date(deadlineIso).getTime() - Date.now()) / 60_000)) : null;
  const closingSoon = minutesLeft != null && minutesLeft <= 120;

  let tone = 'green';
  if (ratio === 0) tone = 'red';
  else if (ratio >= 1) tone = 'green';
  else if (ratio >= 0.6) tone = 'amber';
  else tone = closingSoon ? 'red' : 'amber';

  const stroke =
    tone === 'green' ? 'stroke-emerald-500' :
    tone === 'amber' ? 'stroke-amber-500'  :
                        'stroke-rose-500';
  const text =
    tone === 'green' ? 'text-emerald-700 dark:text-emerald-300' :
    tone === 'amber' ? 'text-amber-700 dark:text-amber-300'   :
                        'text-rose-700 dark:text-rose-300';
  const Icon = tone === 'red' ? AlertTriangle : null;

  const minLabel = min > 0 ? ` · min ${min} needed` : '';
  const tooltip =
    ratio >= 1
      ? `${confirmed}/${denom} in${minLabel} — quorum met`
      : tone === 'red'
        ? `${confirmed}/${denom} in${minLabel}${closingSoon ? ' · closes soon' : ''}`
        : `${confirmed}/${denom} in${minLabel}`;

  return (
    <div className="flex flex-col items-center gap-0.5 shrink-0" title={tooltip} data-testid="rail-quorum-ring" data-quorum-tone={tone}>
      <div className="relative w-6 h-6">
        <svg viewBox="0 0 24 24" className="w-6 h-6 -rotate-90">
          <circle cx="12" cy="12" r={radius} className="fill-none stroke-current text-zinc-200 dark:text-zinc-700" strokeWidth="3" />
          <circle
            cx="12"
            cy="12"
            r={radius}
            className={`fill-none ${stroke} transition-[stroke-dashoffset] duration-500 ease-out`}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
          />
        </svg>
        {Icon && (
          <Icon className={`absolute inset-0 m-auto w-3 h-3 ${text}`} strokeWidth={2.5} />
        )}
      </div>
      <span className={`text-xs font-bold tabular-nums ${text}`}>
        {confirmed}/{denom}
      </span>
    </div>
  );
}

/* ------------------------- Live card ------------------------- */
function LiveCard({ s, variant, onClick }) {
  const isGroup = variant === 'group';
  const liveInClock = s.im_in ? formatRsvpClock(s.my_rsvp_at) : null;
  const liveInTitle = liveInClock ? `In @ ${liveInClock}` : 'In';
  return (
    <Card
      onClick={onClick}
      className={`p-${isGroup ? '4' : '3'} rounded-2xl cursor-pointer transition-transform active:scale-[0.99] ${liveStyleClass(s.live_indicator_style)}`}
      data-testid={`rail-card-live-${s.session_id}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-red-500/20 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
          <Zap className="w-4.5 h-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span>
            </span>
            <span className="text-xs font-bold uppercase tracking-wider text-red-700 dark:text-red-300">LIVE NOW</span>
            <span className="text-xs text-muted-foreground">· {formatElapsed(s.start_iso)}</span>
            {s.im_in && (
              <span
                className="text-xs font-bold px-1.5 py-px rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-0.5"
                title={liveInTitle}
                data-testid={`rail-live-in-${s.session_id}`}
              >
                ✓ IN
              </span>
            )}
          </div>
          <p className={`${isGroup ? 'font-display font-bold' : 'text-sm font-bold'} truncate`}>
            {isGroup ? s.title : `${s.group_name} · ${s.title}`}
          </p>
          <p className="text-xs text-muted-foreground">
            Started at {s.start_time}
            {s.headcount > 0 && ` · ${s.headcount}${s.max_players ? `/${s.max_players}` : ''} playing`}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </div>
    </Card>
  );
}

/* ------------------------- RSVP-open card ------------------------- */
function RsvpOpenCard({ s, variant, acting, onCardClick, onIn, onOut }) {
  const isGroup = variant === 'group';
  const lockedInClock = s.im_in ? formatRsvpClock(s.my_rsvp_at) : null;
  const inBtnTitle = lockedInClock ? `Locked in at ${lockedInClock}` : undefined;
  return (
    <Card
      className={`p-${isGroup ? '4' : '3'} rounded-2xl ${openCardClass('open')}`}
      data-testid={`rail-card-rsvp-${s.session_id}`}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={onCardClick}
          className="flex items-center gap-3 min-w-0 flex-1 text-left"
          data-testid={`rail-rsvp-open-${s.session_id}`}
        >
          <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0">
            <Clock className="w-4.5 h-4.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">RSVP OPEN</span>
              {s.rsvp_closes_at && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-0.5">
                  · closes in <CountdownChip seconds={secondsUntil(s.rsvp_closes_at)} testId={`rail-rsvp-closes-${s.session_id}`} />
                </span>
              )}
            </div>
            <p className={`${isGroup ? 'font-display font-bold' : 'text-sm font-bold'} truncate`}>
              {isGroup ? s.title : `${s.group_name} · ${s.title}`}
            </p>
            <p className="text-xs text-muted-foreground inline-flex items-center gap-0.5 flex-wrap">
              {s.start_time} · starts in <CountdownChip seconds={secondsUntil(s.start_iso)} testId={`rail-rsvp-startsin-${s.session_id}`} />
            </p>
          </div>
        </button>
        <QuorumRing
          confirmed={s.confirmed_count}
          min={s.min_players}
          total={s.total_members}
          deadlineIso={s.rsvp_closes_at || s.start_iso}
        />
        <div className="flex flex-col gap-1.5 shrink-0">
          <button
            type="button"
            onClick={onIn}
            disabled={acting === `${s.session_id}:in` || s.rsvp_locked}
            data-testid={`rail-rsvp-in-${s.session_id}`}
            title={inBtnTitle}
            className={`px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${
              s.im_in
                ? 'bg-emerald-500 text-white'
                : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 hover:bg-emerald-500/25'
            } disabled:opacity-50`}
          >
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> {s.im_in ? 'In' : "I'm In"}
            </span>
          </button>
          <button
            type="button"
            onClick={onOut}
            disabled={acting === `${s.session_id}:out` || s.rsvp_locked}
            data-testid={`rail-rsvp-out-${s.session_id}`}
            className={`px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${
              s.im_out
                ? 'bg-rose-500 text-white'
                : 'bg-rose-500/10 text-rose-700 dark:text-rose-200 hover:bg-rose-500/20'
            } disabled:opacity-50`}
          >
            <span className="inline-flex items-center gap-1">
              <XCircle className="w-3 h-3" /> Out
            </span>
          </button>
        </div>
      </div>
    </Card>
  );
}

/* ------------------------- Opening-soon card ------------------------- */
function OpeningSoonCard({ s, variant, onClick }) {
  const isGroup = variant === 'group';
  return (
    <Card
      onClick={onClick}
      className={`p-${isGroup ? '4' : '3'} rounded-2xl cursor-pointer transition-transform active:scale-[0.99] ${openCardClass('soon')}`}
      data-testid={`rail-card-soon-${s.session_id}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0">
          <Clock className="w-4.5 h-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 inline-flex items-center gap-1">
              OPENS IN <CountdownChip seconds={secondsUntil(s.rsvp_opens_at)} testId={`rail-soon-opensin-${s.session_id}`} />
            </span>
          </div>
          <p className={`${isGroup ? 'font-display font-bold' : 'text-sm font-bold'} truncate`}>
            {isGroup ? s.title : `${s.group_name} · ${s.title}`}
          </p>
          <p className="text-xs text-muted-foreground inline-flex items-center gap-0.5 flex-wrap">
            {s.start_time} · starts in <CountdownChip seconds={secondsUntil(s.start_iso)} testId={`rail-soon-startsin-${s.session_id}`} />
          </p>
        </div>
        <QuorumRing
          confirmed={s.confirmed_count}
          min={s.min_players}
          total={s.total_members}
          deadlineIso={s.rsvp_closes_at || s.start_iso}
        />
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </div>
    </Card>
  );
}
